import React, { useEffect, useState } from 'react'
import {
	AdjustmentsHorizontalIcon,
	BookmarkIcon,
	ChevronDownIcon,
	FolderOpenIcon,
} from '@heroicons/react/24/outline'
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid'
import { useMutation, useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { getConvexToken } from '../../convex'

import SidePanel from '../SidePanel/SidePanel'
import AuthNavBar from '../Dashboard/AuthNavBar'
import SearchTabs from './SearchTabs'
import SearchFilters from './SearchFilters'
import LoadsTable from './LoadsTable'
import FiltersModal from './FiltersModal'
import FilterChips from './FilterChips'
import AutoRefreshControl from './AutoRefreshControl'
import SavedSearchesModal from './SavedSearchesModal'
import DuplicateTabDialog from './DuplicateTabDialog'
import ExtensionRequiredModal from './ExtensionRequiredModal'
import LiveLoadFetchingModal from './LiveLoadFetchingModal'
import TabMatchTracker from './TabMatchTracker'
import { useExtensionStatus } from '../../context/ExtensionStatusContext'
import { callExtension } from '../../utils/extensionBridge'
import useSearchTabs, { filterSignatureLoose } from './useSearchTabs'
import useLoads from './useLoads'
import useSavedSearches from './useSavedSearches'
import { LoadboardThemeProvider, useLoadboardTheme } from './loadboardTheme'
import useNotificationSound from '../../hooks/useNotificationSound'
import { NotificationToastContainer } from '../NotificationToast'
import {
	LIVE_LOAD_FETCHING_KEY,
	useMe,
} from '../../context/MeContext'
import { fetchWithAuth } from '../../utils/apiClient'

const deriveTitle = filters => filters.origin?.trim() || 'Anywhere'
const deriveSubtitle = filters => filters.destination?.trim() || 'Anywhere'
const LIVE_LOAD_PROMPT_DISMISSED_KEY = 'loadboard:liveLoadPromptDismissed'

function LoadboardInner() {
	const [isPanelOpen, setIsPanelOpen] = useState(false)
	const [isManageSearchesOpen, setIsManageSearchesOpen] = useState(false)
	const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)
	const [extensionDismissed, setExtensionDismissed] = useState(false)
	const [showLiveFetchingPrompt, setShowLiveFetchingPrompt] = useState(false)
	const [applyConflict, setApplyConflict] = useState(null)
	const { me, refresh: refreshMe } = useMe()
	const [extensionSyncEnabled, setExtensionSyncEnabled] = useState(
		() => localStorage.getItem(LIVE_LOAD_FETCHING_KEY) === 'true',
	)

	const {
		status: extensionStatus,
		loading: extensionLoading,
		recheck: recheckExtension,
	} = useExtensionStatus()
	const extensionBlocking =
		!extensionLoading && extensionStatus && !extensionStatus.compatible
	const activeSyncRef = React.useRef(null)

	const {
		tabs,
		activeTab,
		activeTabId,
		addTab,
		closeTab,
		setActiveTab,
		updateActiveTab,
		error: tabError,
		duplicateOf,
		cancelDuplicate,
		switchToDuplicate,
	} = useSearchTabs()

	useEffect(() => {
		activeSyncRef.current = activeTab
			? {
					tabId: activeTab.id,
					filters: {
						origin: activeTab.filters.origin,
						originLat: activeTab.filters.originLat,
						originLng: activeTab.filters.originLng,
						originDh: activeTab.filters.originDh,
					},
				}
			: null
	}, [activeTab])

	useEffect(() => {
		if (!me) return
		const enabled = me.liveLoadFetching !== false
		localStorage.setItem(LIVE_LOAD_FETCHING_KEY, String(enabled))
		setExtensionSyncEnabled(enabled)
	}, [me])

	useEffect(() => {
		if (
			me?.liveLoadFetching === false &&
			extensionStatus?.compatible &&
			localStorage.getItem(LIVE_LOAD_PROMPT_DISMISSED_KEY) !== 'true' &&
			activeTab?.filters.origin?.trim()
		) {
			setShowLiveFetchingPrompt(true)
		}
	}, [
		me,
		extensionStatus?.compatible,
		activeTab?.id,
		activeTab?.filters.origin,
	])

	useEffect(() => {
		if (!extensionStatus?.compatible || !extensionSyncEnabled) return
		const requestSync = () => {
			const request = activeSyncRef.current
			if (!request) return
			if (!request.filters.origin?.trim()) {
				console.log('[Load sync cron] skipped: active tab has no origin')
				return
			}
			console.log('[Load sync cron] sending active-tab filter to extension', {
				tabId: request.tabId,
				origin: request.filters.origin,
				originRadius: request.filters.originDh,
			})
			callExtension('LOAD_SYNC_FILTER', request).catch(error =>
				console.warn('Extension filter sync failed:', error.message)
			)
		}
		const timer = setInterval(requestSync, 30_000)
		return () => clearInterval(timer)
	}, [extensionStatus?.compatible, extensionSyncEnabled])

	const handleExtensionSyncEnabledChange = React.useCallback(async enabled => {
		setExtensionSyncEnabled(enabled)
		localStorage.setItem(LIVE_LOAD_FETCHING_KEY, String(enabled))
		try {
			const res = await fetchWithAuth('/api/user/me', {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ liveLoadFetching: enabled }),
			})
			if (!res.ok) throw new Error(`Could not save preference (${res.status})`)
			await refreshMe()
		} catch (error) {
			console.warn('Could not save extension sync setting:', error.message)
			const previousValue = !enabled
			localStorage.setItem(LIVE_LOAD_FETCHING_KEY, String(previousValue))
			setExtensionSyncEnabled(previousValue)
		}
	}, [refreshMe])

	const { theme, tokens } = useLoadboardTheme()
	const isLight = theme === 'light'

	const { loads: restLoads, hasMore, loading, refetch, error: loadsError, isPlaceholderData } = useLoads(activeTab.filters)

	// ============================================================================
	// NOTIFICATION FEATURES: Sound and Popup
	// ============================================================================
	const { playNotificationSound } = useNotificationSound()
	const [popupNotifications, setPopupNotifications] = useState([])

	// ============================================================================
	// CONVEX: Get token and filters for marking notifications as read
	// ============================================================================
	const token = getConvexToken()
	const filters = useQuery(
		api.filters.getFiltersByUser,
		token ? { token } : 'skip',
	)
	const markNotificationsAsRead = useMutation(api.notifications.markFilterNotificationsAsRead)

	// ============================================================================
	// MULTI-TAB REALTIME: Track Convex load IDs for highlighting
	// ============================================================================
	// Per-tab Convex load IDs (for highlighting): { [tabId]: Set<string> }
	// Key format: "source:loadId"
	const [convexLoadIdsByTab, setConvexLoadIdsByTab] = useState({})
	// Per-tab new load counts (for badge display): { [tabId]: number }
	const [newCountsByTab, setNewCountsByTab] = useState({})

	const makeLoadKey = (source, loadId) => `${source || ''}:${loadId}`

	// Callback: Handle new notification matches from any tab's TabMatchTracker
	// Now receives: (tabId, loadIds, isActive, unreadCount, newNotifications)
	const handleNewMatches = React.useCallback((tabId, loadIds, isActive, unreadCount, newNotifications = []) => {


		// Track load IDs for highlighting (just store the load IDs from notifications)
		setConvexLoadIdsByTab(prev => {
			const existing = prev[tabId] || new Set()
			const updated = new Set(existing)
			let hasChanges = false

			loadIds.forEach(loadId => {
				if (!updated.has(loadId)) {
					updated.add(loadId)
					hasChanges = true
				}
			})

			if (!hasChanges) return prev



			return {
				...prev,
				[tabId]: updated,
			}
		})

		// If tab is INACTIVE, show the unread notification count as badge
		if (!isActive && unreadCount > 0) {
			setNewCountsByTab(prev => ({
				...prev,
				[tabId]: unreadCount,
			}))
		}

		// ============================================================================
		// NOTIFICATION FEATURES: Play sound and show popup for new matches
		// ============================================================================
		if (loadIds.length > 0) {
			// Play notification sound
			playNotificationSound()

			// Only add to popup queue if popups are enabled (prevents memory leak)
			const settings = localStorage.getItem('loadboard:notificationSettings')
			const popupEnabled = settings ? JSON.parse(settings).popupEnabled ?? false : false

			if (popupEnabled) {
				// Add notifications to popup queue
				// Find the tab name for display
				const tab = tabs.find(t => t.id === tabId)
				const filterName = tab ? `${tab.title} → ${tab.subtitle}` : 'Unknown Filter'

				// Create popup notifications from the new notification data
				const popupData = newNotifications
					.filter(n => n.load)
					.map((notification, index) => ({
						id: notification._id,
						filterName,
						loadDetails: {
							loadId: notification.load.loadId || 'N/A',
							origin: {
								city: notification.load.originCity,
								state: notification.load.originState
							},
							destination: {
								city: notification.load.deliveryCity,
								state: notification.load.deliveryState
							},
							rate: notification.load.price,
							pickupDate: notification.load.pickupDate,
							equipment: notification.load.equipmentType
						}
					}))

				if (popupData.length > 0) {
					setPopupNotifications(prev => [...prev, ...popupData].slice(-5)) // cap queue: bound bursts & duration=0 ("never close")
				}
			}
		}
	}, [playNotificationSound, tabs])

	// Handler to dismiss popup notifications
	const handleDismissNotification = React.useCallback((notificationId) => {
		setPopupNotifications(prev => prev.filter(n => n.id !== notificationId))
	}, [])

	// Track previous active tab for cleanup when switching away
	const prevActiveTabIdRef = React.useRef(activeTabId)

	// When switching tabs: clear badge of NEW tab immediately, clear Convex state of OLD tab after delay
	useEffect(() => {
		const prevTabId = prevActiveTabIdRef.current
		const currentTabId = activeTabId

		// Find the Convex filter for the current tab
		const convexFilter = filters?.find(
			f => f.filterId === currentTabId
		)

		// Mark notifications as read in Convex for the newly active tab
		if (token && convexFilter) {
			markNotificationsAsRead({
				token,
				filterId: convexFilter._id,
			}).catch(err => {
				console.error('Failed to mark notifications as read:', err)
			})
		}

		// Clear the badge count for the newly active tab (immediate)
		setNewCountsByTab(prev => {
			if (!prev[currentTabId]) return prev
			const next = { ...prev }
			delete next[currentTabId]
			return next
		})

		// Clear the Convex load IDs of the PREVIOUS tab after a brief delay
		// This allows the user to see the green borders when they arrive at the current tab,
		// then clears the old tab's state so it starts fresh counting when it becomes inactive
		const timeoutId = setTimeout(() => {
			if (prevTabId !== currentTabId) {
				setConvexLoadIdsByTab(prev => ({
					...prev,
					[prevTabId]: new Set(),
				}))
			}
		}, 100)

		// Update ref for next switch
		prevActiveTabIdRef.current = currentTabId

		return () => clearTimeout(timeoutId)
	}, [activeTabId, token, filters, markNotificationsAsRead])

	// When a tab is closed, clean up its state
	useEffect(() => {
		const tabIds = new Set(tabs.map(t => t.id))
		setConvexLoadIdsByTab(prev => {
			const next = {}
			for (const id of Object.keys(prev)) {
				if (tabIds.has(id)) next[id] = prev[id]
			}
			return next
		})
		setNewCountsByTab(prev => {
			const next = {}
			for (const id of Object.keys(prev)) {
				if (tabIds.has(id)) next[id] = prev[id]
			}
			return next
		})
	}, [tabs])

	// Highlight PostgreSQL loads that exist in Convex (new loads)
	const convexLoadIds = convexLoadIdsByTab[activeTabId] || new Set()
	const loads = React.useMemo(() => {
		const base = restLoads || []
		if (convexLoadIds.size === 0) return base

		// Log PostgreSQL load IDs
		const postgresIds = base.map(load => makeLoadKey(load.source, load.loadId))

		// Mark loads as new if their ID is in Convex set (green border highlight)
		const result = base.map(load => {
			const key = makeLoadKey(load.source, load.loadId)
			const isMatch = convexLoadIds.has(key)
			if (isMatch) {
				return { ...load, isNew: true }
			}
			return load
		})

		// Summary
		const matchedCount = result.filter(l => l.isNew).length

		return result
	}, [restLoads, convexLoadIds])

	const { saved, addSaved, removeSaved } = useSavedSearches()

	const [saveError, setSaveError] = useState(null)
	useEffect(() => {
		if (!saveError) return
		const id = setTimeout(() => setSaveError(null), 4000)
		return () => clearTimeout(id)
	}, [saveError])

	const handleSaveSearch = async filters => {
		setSaveError(null)
		const result = await addSaved(filters)
		if (result && !result.ok) setSaveError(result.message)
	}

	const apiErrorMessage = loadsError
		? loadsError.response?.status === 429
			? 'Too many requests — slow down and try again in a moment.'
			: 'Could not load loads. Check your connection and try again.'
		: null
	const toastMessage = tabError || apiErrorMessage

	const handleFiltersChange = nextFilters => {
		if (
			me?.liveLoadFetching === false &&
			extensionStatus?.compatible &&
			localStorage.getItem(LIVE_LOAD_PROMPT_DISMISSED_KEY) !== 'true' &&
			nextFilters.origin?.trim()
		) {
			setShowLiveFetchingPrompt(true)
		}
		updateActiveTab({
			filters: nextFilters,
			title: deriveTitle(nextFilters),
			subtitle: deriveSubtitle(nextFilters),
		})
	}

	const applySavedSearch = picked => {
		const pickedSig = filterSignatureLoose(picked)
		const existing = tabs.find(t => filterSignatureLoose(t.filters) === pickedSig)
		if (existing) {
			setApplyConflict({ existing: { id: existing.id, title: existing.title } })
			return
		}
		addTab({
			filters: picked,
			title: deriveTitle(picked),
			subtitle: deriveSubtitle(picked),
		})
	}

	const handleCloseTab = tabId => {
		closeTab(tabId)
	}

	const handleSwitchToDuplicate = () => {
		const oldActiveId = activeTabId
		switchToDuplicate()
		handleCloseTab(oldActiveId)
	}

	const tableToolbar = (
		<>
			<div className="relative">
				<button
					type="button"
					data-filters-trigger
					onClick={() => setIsFiltersModalOpen(v => !v)}
					className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12.5px] font-medium transition-colors ${
						isLight
							? 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900'
							: 'bg-white/[0.05] text-gray-300 hover:bg-white/[0.1] hover:text-gray-100'
					}`}
				>
					<AdjustmentsHorizontalIcon className="h-3.5 w-3.5" strokeWidth={2} />
					Filters
					<ChevronDownIcon className="h-3 w-3" strokeWidth={2} />
				</button>
				<FiltersModal
					isOpen={isFiltersModalOpen}
					onClose={() => setIsFiltersModalOpen(false)}
					filters={activeTab.filters}
					onChange={handleFiltersChange}
				/>
			</div>
			<FilterChips filters={activeTab.filters} onChange={handleFiltersChange} />
		</>
	)

	return (
		<div className={`${tokens.pageBg} ${tokens.pageText} min-h-screen relative`}>
			{/* Tab-level warnings (duplicate / limit) always toast. API errors
				 only toast when loads are visible — when the table is empty + errored
				 the in-table warning already carries the message. */}
			{toastMessage && (tabError || loads.length > 0) && (
				<div
					role="alert"
					className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md flex items-start gap-2 px-4 py-2.5 rounded-md text-sm font-medium shadow-lg ring-1 border-l-4 transition-opacity ${
						isLight
							? 'bg-amber-50 text-amber-900 ring-amber-200 border-amber-500'
							: 'bg-amber-500/15 text-amber-200 ring-amber-400/30 border-amber-400'
					}`}
				>
					<ExclamationTriangleIcon className={`h-5 w-5 shrink-0 mt-px ${isLight ? 'text-amber-600' : 'text-amber-400'}`} />
					<span>{toastMessage}</span>
				</div>
			)}

			{saveError && (
				<div
					role="alert"
					className={`fixed top-4 left-1/2 -translate-x-1/2 z-[60] max-w-md flex items-start gap-2 px-4 py-2.5 rounded-md text-sm font-medium shadow-lg ring-1 border-l-4 transition-opacity ${
						isLight
							? 'bg-rose-50 text-rose-900 ring-rose-200 border-rose-500'
							: 'bg-rose-500/15 text-rose-200 ring-rose-400/30 border-rose-400'
					}`}
				>
					<ExclamationTriangleIcon
						className={`h-5 w-5 shrink-0 mt-px ${isLight ? 'text-rose-600' : 'text-rose-400'}`}
					/>
					<span>{saveError}</span>
				</div>
			)}
			<AuthNavBar compact>
				<div className="py-2">
					<SearchTabs
						tabs={tabs}
						activeTabId={activeTabId}
						onSelect={setActiveTab}
						onClose={handleCloseTab}
						onAdd={addTab}
						newCountsByTab={newCountsByTab}
					/>
				</div>
			</AuthNavBar>
			<SidePanel
				isOpen={isPanelOpen}
				onOpen={() => setIsPanelOpen(true)}
				onClose={() => setIsPanelOpen(false)}
			/>

			{/* Multi-tab realtime subscriptions (invisible components) */}
			{/* Keep ALL tabs mounted to preserve match history - only inactive tabs report new counts */}
			{tabs.map(tab => (
				<TabMatchTracker
					key={tab.id}
					tabId={tab.id}
					isActive={tab.id === activeTabId}
					onNewMatches={handleNewMatches}
				/>
			))}

			{/* Main content area with notifications sidebar */}
			<div className="flex gap-4">
				{/* Main content column */}
				<div className="flex-1 min-w-0">
					<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-1">
						<div className="flex items-center justify-between mb-1">
							<div className="flex items-center gap-2">
								<button
									type="button"
									onClick={() => setIsManageSearchesOpen(true)}
									className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12.5px] font-medium transition-colors ${
										isLight
											? 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
											: 'text-gray-300 hover:bg-white/[0.08] hover:text-gray-100'
									}`}
								>
									<FolderOpenIcon className="h-3.5 w-3.5" strokeWidth={2} />
									Saved searches
								</button>
								<button
									type="button"
									onClick={() => handleSaveSearch(activeTab.filters)}
									title="Save current filters as a saved search"
									className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12.5px] font-medium transition-colors ${
										isLight
											? 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
											: 'text-gray-300 hover:bg-white/[0.08] hover:text-gray-100'
									}`}
								>
									<BookmarkIcon className="h-3.5 w-3.5" strokeWidth={2} />
									Save this search
								</button>
							</div>
							<AutoRefreshControl
								tokens={tokens}
								isLight={isLight}
								extensionSyncEnabled={extensionSyncEnabled}
								onExtensionSyncEnabledChange={
									handleExtensionSyncEnabledChange
								}
								onRefresh={() => {
									setConvexLoadIdsByTab(prev => ({
										...prev,
										[activeTabId]: new Set(),
									}))
									refetch()
								}}
							/>
						</div>
						<SearchFilters
							filters={activeTab.filters}
							onChange={handleFiltersChange}
							onSaveSearch={handleSaveSearch}
							onOpenManageSearches={() => setIsManageSearchesOpen(true)}
						/>
					</div>

					<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
						<LoadsTable
							loads={loads}
							hasMore={hasMore}
							loading={loading}
							errorMessage={apiErrorMessage}
							toolbar={tableToolbar}
							sort={activeTab.filters.sort || 'pickup'}
							onSortChange={v => handleFiltersChange({ ...activeTab.filters, sort: v })}
							showArchive={Boolean(activeTab.filters.showArchive)}
							onShowArchiveChange={v =>
								handleFiltersChange({ ...activeTab.filters, showArchive: v })
							}
							searchOrigin={
								activeTab.filters.originLat != null && activeTab.filters.originLng != null
									? { lat: activeTab.filters.originLat, lng: activeTab.filters.originLng }
									: null
							}
						/>
					</div>
				</div>

			</div>

			<SavedSearchesModal
				isOpen={isManageSearchesOpen}
				onClose={() => setIsManageSearchesOpen(false)}
				saved={saved}
				onApply={applySavedSearch}
				onRemoveSaved={removeSaved}
			/>

			{extensionBlocking && !extensionDismissed && (
				<ExtensionRequiredModal
					status={extensionStatus}
					onRecheck={recheckExtension}
					onClose={() => setExtensionDismissed(true)}
				/>
			)}

			{showLiveFetchingPrompt && (
				<LiveLoadFetchingModal
					onClose={dontShowAgain => {
						if (dontShowAgain) {
							localStorage.setItem(LIVE_LOAD_PROMPT_DISMISSED_KEY, 'true')
						}
						setShowLiveFetchingPrompt(false)
					}}
					onEnable={dontShowAgain => {
						if (dontShowAgain) {
							localStorage.setItem(LIVE_LOAD_PROMPT_DISMISSED_KEY, 'true')
						}
						setShowLiveFetchingPrompt(false)
						handleExtensionSyncEnabledChange(true)
					}}
				/>
			)}

			<DuplicateTabDialog
				duplicateOf={applyConflict ? applyConflict.existing : duplicateOf}
				onSwitch={
					applyConflict
						? () => {
								setActiveTab(applyConflict.existing.id)
								setApplyConflict(null)
						  }
						: handleSwitchToDuplicate
				}
				onCancel={
					applyConflict ? () => setApplyConflict(null) : cancelDuplicate
				}
			/>

			{/* Notification Toast Popups */}
			<NotificationToastContainer
				notifications={popupNotifications}
				onDismiss={handleDismissNotification}
			/>
		</div>
	)
}

function Loadboard() {
	return (
		<LoadboardThemeProvider>
			<LoadboardInner />
		</LoadboardThemeProvider>
	)
}

export default Loadboard
